import {useMemo} from 'react';
import getDashboardUserPermissions from '@/utils/getDashboardUserPermissions';
import {Role} from '@/config/roles';
import {Merchant} from '@/types/agency';
import {DashboardUser} from '@/types/dashboardUser';
import {PermissionObject, RequestedPermission} from '@/types/permissions';
import {Subscription} from '@/types/subscription';
import {MenuItem, DashboardUserCurrentBrand} from '../menuItems';

export interface ResolvedMenuItem {
  item: MenuItem;
  isUnlocked: boolean;
  showUnreadIndicator: boolean;
}

export type ResolvedItem =
  | {type: 'separator'; id: string}
  | ({type: 'item'} & ResolvedMenuItem);

interface UseMenuItemsParams {
  menuItems: MenuItem[];
  user: DashboardUser;
  currentBrand?: Merchant;
  subscription?: Subscription;
  flags: Record<string, boolean>;
  dashboardUserCurrentBrand?: DashboardUserCurrentBrand;
}

function hasAllPermissions(
  required: RequestedPermission[],
  userPermissions?: PermissionObject[]
): boolean {
  if (!required.length) return true;
  const perms = getDashboardUserPermissions(required, userPermissions);
  return Object.values(perms).every(Boolean);
}

function hasRequiredRoles(user: DashboardUser, requiredRoles: Role[]): boolean {
  if (!requiredRoles.length) {
    return true;
  }
  if (!user?.roles?.length) {
    return false;
  }
  const userRoles = new Set(user.roles.map((role: {name: Role}) => role.name));
  return requiredRoles.every((role) => userRoles.has(role));
}

/**
 * Resolves menu items: filters by flags, roles, permissions, and custom logic.
 * Computes isUnlocked and showUnreadIndicator per item.
 * Cleans up leading/trailing/consecutive separators.
 */
export default function useMenuItems({
  menuItems,
  user,
  currentBrand,
  subscription,
  flags,
  dashboardUserCurrentBrand,
}: UseMenuItemsParams): ResolvedItem[] {
  return useMemo(() => {
    const resolved: ResolvedItem[] = [];

    menuItems.forEach((item) => {
      if (item.isSeparator) {
        if (
          resolved.length > 0 &&
          resolved[resolved.length - 1]?.type !== 'separator'
        ) {
          resolved.push({type: 'separator', id: item.id});
        }
        return;
      }

      // Flag check
      if (item.flagName && !flags[item.flagName]) {
        return;
      }

      // Role check
      if (!hasRequiredRoles(user, item.requiredRoles)) {
        return;
      }

      // Display permission check
      if (
        !hasAllPermissions(item.requiredPermissionsToDisplay, user?.permissions)
      ) {
        return;
      }

      // Custom display check
      const customIsDisplayed = item.customIsDisplayedFunc
        ? item.customIsDisplayedFunc({currentBrand, subscription, flags})
        : true;
      if (!customIsDisplayed) {
        return;
      }

      // Unlock permission check
      const isUnlocked = hasAllPermissions(
        item.requiredPermissionsToUnlock,
        user?.permissions
      );

      // Unread indicator
      const showUnreadIndicator = item.showUnreadIndicatorFunc
        ? item.showUnreadIndicatorFunc(dashboardUserCurrentBrand)
        : false;

      resolved.push({
        type: 'item',
        item,
        isUnlocked,
        showUnreadIndicator,
      });
    });

    // Remove trailing separator
    if (
      resolved.length > 0 &&
      resolved[resolved.length - 1]?.type === 'separator'
    ) {
      resolved.pop();
    }

    return resolved;
  }, [
    menuItems,
    flags,
    user,
    currentBrand,
    subscription,
    dashboardUserCurrentBrand,
  ]);
}
